import Box from "@mui/material/Box";
import { Button, Dialog, DialogActions, DialogTitle, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useContext } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { GridToolbarContainer, GridActionsCellItem } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import TextField from '@mui/material/TextField';
import { ApiContext } from "../../../App";


const ControllerPopUp = ({ title, popUpData, setPopUpData, dialogIsOpen, setDialogIsOpen, cancelHandler, saveHandler, errorMessages, setErrorMessages }) => {
    const { t } = useTranslation();

    const device_types = ["bosch", "fieldserver", "kepserver", "shelly", "disruptive", "emporia", "airthings"]

    const formHandler = (event) => {
        event.preventDefault()

        const errors = {}
        if (event.target.name.value === "") {
            errors['name'] = 'Name must have a value'
        }
        if (event.target.address.value === "") {
            errors['address'] = 'Address must have a value'
        }
        if (!event.target[1].value) {
            errors['device_type'] = 'Device type must have a value'
        }
        setErrorMessages(errors)

        if (Object.keys(errors).length === 0) {
            saveHandler(event)
        }
    }


    return (
        <Dialog open={dialogIsOpen} fullWidth maxWidth={"md"} onClose={() => setDialogIsOpen(!dialogIsOpen)}>
            <form onSubmit={formHandler}>
                <Stack direction='column' spacing={2} sx={{ paddingLeft: '5%', paddingRight: '5%' }}>
                    <DialogTitle sx={{ padding: '16px 0px', paddingBottom: '0' }}>{title}</DialogTitle>
                    <TextField
                        style={{ marginTop: '0' }}
                        variant="standard"
                        id="name"
                        label={t("Name")}
                        value={popUpData.name}
                        onChange={(event) => setPopUpData({ ...popUpData, name: event.target.value })}
                        error={errorMessages.hasOwnProperty("name")}
                        helperText={errorMessages["name"] ? errorMessages["name"] : ""}
                    />
                        <Stack direction="column" width="100%">
                            <label style={{ fontSize: '0.75em', color: 'grey' }}>{t("Device Type")}</label>
                            <select
                                value={popUpData.device_type} 
                                onChange={(event) => setPopUpData({ ...popUpData, device_type: event.target.value })}
                                style={{ height: '30px', border: 'none', borderBottom: "1px solid grey", fontSize: '16px', textAlign: 'left' }}
                            >
                                {
                                    device_types.map((device_type, index) => (
                                        <option value={device_type} key={index} >{device_type}</option>
                                    ))
                                }
                            </select>
                        </Stack>
                    <TextField
                        style={{ marginTop: '0' }}
                        variant="standard"
                        id="address"
                        label={t("Address")}
                        value={popUpData.address}
                        onChange={(event) => setPopUpData({ ...popUpData, address: event.target.value })}
                        error={errorMessages.hasOwnProperty("address")}
                        helperText={errorMessages["address"] ? errorMessages["address"] : ""}
                    />
                    <DialogActions>
                        <Button onClick={cancelHandler} type="button">Cancel</Button>
                        <Button type="submit">Save</Button>
                    </DialogActions>
                </Stack>
            </form>
        </Dialog>
    )
}


const ControllerTable = ({ api, facility }) => {
    const { t } = useTranslation();
    const [errorMessages, setErrorMessages] = useState({});
    const [controllerRows, setControllerRows] = useState([])
    const [dialogIsOpen, setDialogIsOpen] = useState(false)
    const [dialogTitle, setDialogTitle] = useState('')
    const [popUpData, setPopUpData] = useState({
        name: "",
        address: "",
        device_type: "bosch",
    })
    
    const {controllerApi} = useContext(ApiContext)

    useEffect(() => {
        controllerApi.list(facility,
            data => {
                setControllerRows(data)
            },
            () => setErrorMessages(t("Couldn't connect to server")))
    }, [facility])

    const deleteHandler = (id) => {
        const confirmed = window.confirm(t('Delete this controller?'))
        if (confirmed) {
            controllerApi.delete(id, () => setControllerRows(controllerRows.filter((row) => row.url !== id)), () => alert(t('Controller failed to be deleted')))
        }
    }

    const editControllerHandler = (id) => {
        setErrorMessages({})
        setDialogIsOpen(!dialogIsOpen)
        setDialogTitle('Edit Controller')
        setPopUpData(controllerRows.find((row) => row.url === id))
    }

    const cancelHandler = () => {
        setDialogIsOpen(!dialogIsOpen)
    }

    const dialogHandler = (event) => {
        event.preventDefault()
        if (dialogTitle === 'Add Controller') {
            const newController = { ...popUpData, facility }
            controllerApi.create(newController, (data) => {
                setControllerRows([...controllerRows, { ...newController, url: data.url }])
                setDialogIsOpen(!dialogIsOpen)
            }, () => {
                alert(t('failed to create controller'))
            })
        }
        if (dialogTitle === 'Edit Controller') {
            setDialogIsOpen(!dialogIsOpen)
            controllerApi.update(popUpData.url, popUpData, () => setControllerRows(controllerRows.map((row) => popUpData.url === row.url ? popUpData : row)), () => alert(t('Failed to edit controller')))
        }

    }

    const addControllerHandler = () => {
        setErrorMessages({})
        setDialogTitle('Add Controller')
        setPopUpData({
            name: '',
            device_type: 'bosch',
            address: ''
        })
        setDialogIsOpen(!dialogIsOpen)
    }


    const columns = [
        {
            field: "name",
            headerName: t("Name"),
            width: 350
        },
        {
            field: "device_type",
            headerName: t("Device Type"),
            width: 350
        },
        {
            field: "address",
            headerName: t("Address"),
            width: 350
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: t('Actions'),
            flex: 0.5,
            minWidth: 150,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label={t("Edit")}
                        className="textPrimary"
                        onClick={() => editControllerHandler(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label={t("Delete")}
                        onClick={() => deleteHandler(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ]

    const gridToolbar = () => {
        return (
            <GridToolbarContainer>
                <Button color="primary" size="small" startIcon={<AddIcon />} onClick={addControllerHandler}>
                    {t("Add Controller")}
                </Button>
            </GridToolbarContainer>
        );
    }


    return (
        <Box sx={{ height: 400, width: '100%', marginTop: '20px' }}>
            <DataGrid
                getRowId={(row) => row.url}
                components={{
                    Toolbar: gridToolbar,
                }}
                rows={controllerRows}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 5,
                        },
                    },
                }}
                pageSizeOptions={[5]}
            />
            <ControllerPopUp setDialogIsOpen={setDialogIsOpen} title={dialogTitle} errorMessages={errorMessages} setErrorMessages={setErrorMessages} dialogIsOpen={dialogIsOpen} setPopUpData={setPopUpData} popUpData={popUpData} cancelHandler={cancelHandler} saveHandler={dialogHandler} />
        </Box>
    );
}

export default ControllerTable;